import React, { useCallback, useRef, useState } from "react";
import "./students.component.scoped.scss";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@material-ui/lab";
import SearchIcon from "@material-ui/icons/Search";
import { icons } from "../../../../statics/icons";

interface StudentsComponentProps {
  loading: boolean;
  users: any[];
  title: string;
  onFilterChange: (filters: any) => void;
}

const StudentsComponent = (props: StudentsComponentProps) => {
  const { t } = useTranslation();
  const { loading, users, onFilterChange, title } = props;

  const [userImageError, setUserImageError] = useState(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Handle search text change with debouncing
   */
  const onSearchChange = useCallback(
    (searchText: string) => {
      // Update input immediately for responsive UI
      setSearchInput(searchText);

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout to update filters after user stops typing (600ms)
      searchTimeoutRef.current = setTimeout(() => {
        onFilterChange({ searchText });
      }, 600);
    },
    [onFilterChange],
  );

  return (
    <Box className="students__content">
      <Box className="global__box__content p-0">
        <Box className="global__box__content__header">
          <Box className="d-flex align-items-center" style={{ gap: "1.5rem" }}>
            {loading ? (
              <Skeleton variant="text" height={20} width={200} />
            ) : (
              <Typography variant="h3">{title}</Typography>
            )}
            {loading ? (
              <Skeleton variant="text" height={18} width={150} />
            ) : (
              <Typography variant="caption">
                {users.length} {t("EDUCATION_PAGE.COURSE_FINISHED")}
              </Typography>
            )}
          </Box>
          <Box className="standard_text_input" display="flex">
            <TextField
              variant="standard"
              type="text"
              autoComplete="off"
              onChange={(e: any) => onSearchChange(e.target.value)}
              value={searchInput}
              placeholder={t("EDUCATION_PAGE.SEARCH_USERS")}
              className="w-100"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
        <Box className="global__box__content__body">
          {users.length === 0 && !loading && (
            <Box className="my-5 text-center">
              <Typography variant="h3" style={{ color: "#78829D" }}>
                {t("EDUCATION_PAGE.NO_FINISHED_USERS_FOUND")}
              </Typography>
            </Box>
          )}
          <Box className="students__list py-3">
            {users.map((user) => (
              <Box className="student__card" key={user.id}>
                <Box className="user__profile__pic">
                  {user.profilePicURL && !userImageError ? (
                    <img
                      src={user.profilePicURL || icons.PLACEHOLDER.user}
                      title={user.firstName + " " + user.lastName}
                      alt=""
                      onError={setUserImageError.bind(null, true)}
                    />
                  ) : (
                    <span className="text-uppercase">
                      {user.firstName
                        ? user.firstName[0] + user.lastName[0]
                        : ""}
                    </span>
                  )}
                </Box>
                <Box className="student__card__info">
                  <Typography variant="h4">
                    {user.firstName + " " + user.lastName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {user.email}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StudentsComponent;
